import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import Logo from '../assets/Logo';
import { useAppContext } from '../hooks/AppProvider';

const Container = styled('div')({
  position: 'relative',
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  fontSize: '1.75rem',
  zIndex: 3,
  [theme.breakpoints.up('md')]: {
    fontSize: '3rem',
  },
}));

const StyledMenu = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100vw',
  height: '100vh',
  minHeight: '100dvh',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  fontSize: '3rem',
  pointerEvents: 'none',
  zIndex: 2,
  transition: 'opacity 0.75s ease-in-out',
  nav: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1rem',
    minHeight: '100%',
    padding: '5rem 1rem 2rem',
    width: '100%',
  },
});

const StyledLink = styled(Link)(({ theme }) => ({
  fontFamily: 'Usuzi',
  fontSize: 'max(2vw, 16pt)',
  color: theme.palette.text.primary,
  textTransform: 'uppercase',
  textDecoration: 'none',
  '&:hover, &:focus-visible': {
    color: theme.palette.brand.focus,
  },
}));

const StyledSvg = styled('svg')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: 0,
  height: 0,
  overflow: 'hidden',
  zIndex: 1,
  circle: {
    fill: theme.palette.background.paper,
  },
}));

// create a svg circle
const SvgCircle = () => (
  <StyledSvg
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 100 100"
    preserveAspectRatio="none"
    className="circle"
    role="graphics-symbol"
  >
    <circle cx="50" cy="50" r="50" />
  </StyledSvg>
);

const Menu = () => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const container = React.useRef(null);
  const menuButton = React.useRef(null);
  const menuPanel = React.useRef(null);
  const tl = React.useRef(null);
  const { reducedMotion } = useAppContext();
  const { pathname } = useLocation();
  
  gsap.registerPlugin(useGSAP);

  useGSAP(
    () => {
      const { innerWidth, innerHeight } = window;
      const { top, left, width, height } = menuButton.current.getBoundingClientRect();
      gsap.set('.circle', { top: top + height / 2, left: left + width / 2, width: 0, height: 0 });
      gsap.set('nav a', { opacity: 0, y: 50, pointerEvents: 'none' });
      tl.current = gsap.timeline({ defaults: { ease: 'power2.inOut' }, paused: true });
      tl.current.to('.menu-button', {
        rotation: "+=360",
      });
      const innerMax = Math.max(innerWidth, innerHeight);
      tl.current.to('.circle', {
        top: innerMax / 2 * -1,
        left: innerMax / 2 * -1,
        width: innerMax * 2,
        height: innerMax * 2,
      }, '-=0.5');
      tl.current.to('nav a', {
        stagger: 0.05,
        pointerEvents: 'all',
        opacity: 1,
        y: 0,
      }, '-=0.5');
    },
    { scope: container },
  );

  const { contextSafe } = useGSAP({ scope: container });

  const onEnter = contextSafe(() => {
    setMenuOpen(prev => !prev);
    menuOpen ? tl.current.duration(reducedMotion ? 0 : 1).reverse() : tl.current.duration(reducedMotion ? 0 : 2).play();
  });

  React.useEffect(() => {
    if (!menuOpen) {
      return undefined;
    }

    const backgroundElements = document.querySelectorAll('main, footer');
    const previousOverflow = document.body.style.overflow;
    const links = Array.from(menuPanel.current.querySelectorAll('a'));
    const focusableElements = [menuButton.current, ...links];

    document.body.style.overflow = 'hidden';
    backgroundElements.forEach((element) => {
      element.inert = true;
    });
    links[0]?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMenuOpen(false);
        tl.current.duration(reducedMotion ? 0 : 1).reverse();
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      backgroundElements.forEach((element) => {
        element.inert = false;
      });
      menuButton.current?.focus();
    };
  }, [menuOpen, reducedMotion]);

  return (
    <Container ref={container}>
      <Tooltip title={menuOpen ? "Menü schließen" : "Menü öffnen"}>
        <StyledIconButton
          ref={menuButton}
          onClick={onEnter}
          color="inherit"
          className="menu-button borderBox"
          aria-label={menuOpen ? 'Menü schließen' : 'Menü öffnen'}
          aria-controls="mainmenu"
          aria-expanded={menuOpen}
        >
          {menuOpen ? <CloseIcon fontSize="inherit" /> : <MenuIcon fontSize="inherit" />}
        </StyledIconButton>
      </Tooltip>
      <SvgCircle />
      <StyledMenu ref={menuPanel} id="mainmenu" hidden={!menuOpen}>
        <nav aria-label="Hauptnavigation">
          <StyledLink aria-current={pathname === '/' ? 'page' : undefined} aria-label="Startseite" onClick={onEnter} tabIndex={menuOpen ? 0 : -1} to="/"><Logo /></StyledLink>
          <StyledLink aria-current={pathname === '/administration' ? 'page' : undefined} onClick={onEnter} tabIndex={menuOpen ? 0 : -1} to="/administration">Administration</StyledLink>
          <StyledLink aria-current={pathname === '/software' ? 'page' : undefined} onClick={onEnter} tabIndex={menuOpen ? 0 : -1} to="/software">Software</StyledLink>
          <StyledLink aria-current={pathname === '/hosting' ? 'page' : undefined} onClick={onEnter} tabIndex={menuOpen ? 0 : -1} to="/hosting">Hosting</StyledLink>
          <StyledLink aria-current={pathname === '/impressum' ? 'page' : undefined} onClick={onEnter} tabIndex={menuOpen ? 0 : -1} to="/impressum">Impressum</StyledLink>
          <StyledLink aria-current={pathname === '/datenschutz' ? 'page' : undefined} onClick={onEnter} tabIndex={menuOpen ? 0 : -1} to="/datenschutz">Datenschutz</StyledLink>
        </nav>
      </StyledMenu>
    </Container>
  );
};

export default Menu;
